import { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, LogOut, BarChart3, Search, Flame, Info, Mail, Palette, Settings, Shield, FileText, Users } from 'lucide-react';
import { Toaster } from './components/ui/sonner';
import { Button } from './components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './components/ui/dropdown-menu';

import './lib/i18n';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import SubmitRequestPage from './pages/SubmitRequestPage';
import AdminDashboard from './pages/AdminDashboard';
import AuthorityPanel from './pages/AuthorityPanel';
import SubscriberDashboard from './pages/SubscriberDashboard';
import TrackingPage from './pages/TrackingPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import AffiliateDashboard from './pages/AffiliateDashboard';
import AppealDetailPage from './pages/AppealDetailPage';
import SettingsPage from './pages/SettingsPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import UsagePolicyPage from './pages/UsagePolicyPage';
import { authAPI } from './lib/api';

import '@/App.css';

const THEMES = [
  { id: 'midnight', label: 'Midnight Nebula', icon: '🌌', dark: true },
  { id: 'aurora', label: 'Aurora Frost', icon: '❄️', dark: false },
  { id: 'cyber', label: 'Cyber Neon', icon: '⚡', dark: true },
  { id: 'ember', label: 'Sunset Ember', icon: '🔥', dark: true },
];

export const ThemeContext = createContext({ theme: 'midnight', setTheme: () => {}, themes: THEMES });
export const useTheme = () => useContext(ThemeContext);

function Footer() {
  const { theme } = useTheme();
  const isDark = theme !== 'aurora';

  return (
    <footer className={`border-t transition-colors ${isDark ? 'bg-[#020204] border-white/[0.04]' : 'bg-white border-gray-200'}`}>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          <div className="md:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${isDark ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-blue-500/10 border border-blue-500/20'}`}>
                <Flame className="w-5 h-5 text-blue-500" />
              </div>
              <span className={`text-xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Outfit' }}>
                my<span className="text-blue-500">Act</span>
              </span>
            </div>
            <p className={`text-sm leading-relaxed ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
              European Digital Services Act Appeal Platform. Fight back against unfair content moderation.
            </p>
          </div>
          <div>
            <h4 className={`text-sm font-semibold mb-4 ${isDark ? 'text-white/70' : 'text-gray-700'}`} style={{ fontFamily: 'Outfit' }}>Platform</h4>
            <ul className="space-y-2">
              {[{ to: '/submit', label: 'File Appeal' }, { to: '/track', label: 'Track Appeal' }, { to: '/about', label: 'About Us' }, { to: '/contact', label: 'Contact' }].map(link => (
                <li key={link.to}><Link to={link.to} className={`text-sm transition-colors ${isDark ? 'text-white/40 hover:text-white/70' : 'text-gray-500 hover:text-gray-700'}`}>{link.label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className={`text-sm font-semibold mb-4 ${isDark ? 'text-white/70' : 'text-gray-700'}`} style={{ fontFamily: 'Outfit' }}>Legal</h4>
            <ul className="space-y-2">
              {[{ to: '/terms', label: 'Terms of Service' }, { to: '/privacy', label: 'Privacy Policy' }, { to: '/usage-policy', label: 'Usage Policy' }].map(link => (
                <li key={link.to}><Link to={link.to} className={`text-sm transition-colors ${isDark ? 'text-white/40 hover:text-white/70' : 'text-gray-500 hover:text-gray-700'}`}>{link.label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className={`text-sm font-semibold mb-4 ${isDark ? 'text-white/70' : 'text-gray-700'}`} style={{ fontFamily: 'Outfit' }}>Support</h4>
            <ul className="space-y-2">
              <li className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>support@arbbio.com</li>
              <li className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>EU Business Hours</li>
              <li className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>GDPR Compliant</li>
            </ul>
          </div>
        </div>
        <div className={`border-t pt-6 flex flex-col md:flex-row items-center justify-between gap-4 ${isDark ? 'border-white/[0.04]' : 'border-gray-200'}`}>
          <p className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>&copy; {new Date().getFullYear()} myAct. All rights reserved. EU Digital Services Act Compliance Platform.</p>
          <div className="flex items-center gap-4">
            <Link to="/terms" className={`text-xs transition-colors ${isDark ? 'text-white/30 hover:text-white/50' : 'text-gray-400 hover:text-gray-600'}`}>Terms</Link>
            <Link to="/privacy" className={`text-xs transition-colors ${isDark ? 'text-white/30 hover:text-white/50' : 'text-gray-400 hover:text-gray-600'}`}>Privacy</Link>
            <Link to="/usage-policy" className={`text-xs transition-colors ${isDark ? 'text-white/30 hover:text-white/50' : 'text-gray-400 hover:text-gray-600'}`}>Usage Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function Navbar({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { theme, setTheme, themes } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isLanding = location.pathname === '/';
  const isDark = theme !== 'aurora';

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
    document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lng;
  };

  const getLangLabel = (lng) => ({ en: 'EN', ar: 'AR', de: 'DE' }[lng] || 'EN');
  const currentTheme = themes.find(t => t.id === theme) || themes[0];

  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isLanding && !user ? 'bg-transparent' : isDark ? 'bg-[#020204]/80 backdrop-blur-xl border-b border-white/[0.04]' : 'bg-white/80 backdrop-blur-xl border-b border-black/[0.06]'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${isDark ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-blue-500/10 border border-blue-500/20'}`}>
            <Flame className="w-5 h-5 text-blue-500" />
          </div>
          <span className={`text-xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Outfit' }}>my<span className="text-blue-500">Act</span></span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          <Link to="/track"><Button variant="ghost" size="sm" className={`rounded-lg ${isDark ? 'text-white/50 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}><Search className="w-4 h-4 mr-1.5" /> {t('nav.track')}</Button></Link>
          <Link to="/about"><Button variant="ghost" size="sm" className={`rounded-lg ${isDark ? 'text-white/50 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}><Info className="w-4 h-4 mr-1.5" /> About</Button></Link>
          <Link to="/contact"><Button variant="ghost" size="sm" className={`rounded-lg ${isDark ? 'text-white/50 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}><Mail className="w-4 h-4 mr-1.5" /> Contact</Button></Link>

          {user && (
            <Link to={user.role === 'super_admin' ? '/admin' : user.role === 'authority' ? '/authority' : user.role === 'affiliate' ? '/affiliate' : '/dashboard'}>
              <Button variant="ghost" size="sm" className={`rounded-lg ${isDark ? 'text-white/50 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}><BarChart3 className="w-4 h-4 mr-1.5" /> {t('nav.dashboard')}</Button>
            </Link>
          )}

          {user && user.role === 'super_admin' && (
            <Link to="/settings"><Button variant="ghost" size="sm" className={`rounded-lg ${isDark ? 'text-white/50 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}><Settings className="w-4 h-4 mr-1.5" /> Settings</Button></Link>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className={`rounded-lg ${isDark ? 'text-white/50 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}><Globe className="w-4 h-4 mr-1.5" /> {getLangLabel(i18n.language)}</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className={`min-w-[120px] ${isDark ? 'bg-[#0A0A0C] border-white/10' : 'bg-white border-gray-200'}`}>
              <DropdownMenuItem onClick={() => changeLanguage('en')} className={`cursor-pointer ${isDark ? 'text-white hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50'}`}>English</DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeLanguage('ar')} className={`cursor-pointer ${isDark ? 'text-white hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50'}`}>العربية</DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeLanguage('de')} className={`cursor-pointer ${isDark ? 'text-white hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50'}`}>Deutsch</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <Button variant="ghost" size="sm" onClick={onLogout} className={`rounded-lg ${isDark ? 'text-white/50 hover:text-red-400 hover:bg-red-500/5' : 'text-gray-500 hover:text-red-500 hover:bg-red-50'}`}><LogOut className="w-4 h-4 mr-1.5" /> {t('nav.logout')}</Button>
          ) : (
            <Link to="/auth"><Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white rounded-lg ml-2">{t('nav.login')}</Button></Link>
          )}
        </div>

        <div className="flex md:hidden items-center gap-2">
          <button className={isDark ? 'text-white/60' : 'text-gray-500'} onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className={`md:hidden backdrop-blur-xl border-b ${isDark ? 'bg-[#0A0A0C]/95 border-white/[0.06]' : 'bg-white/95 border-gray-200'}`}>
            <div className="px-6 py-4 space-y-2">
              <Link to="/track" onClick={() => setMobileOpen(false)}><Button variant="ghost" className={`w-full justify-start ${isDark ? 'text-white/50 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>{t('nav.track')}</Button></Link>
              <Link to="/about" onClick={() => setMobileOpen(false)}><Button variant="ghost" className={`w-full justify-start ${isDark ? 'text-white/50 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>About</Button></Link>
              <Link to="/contact" onClick={() => setMobileOpen(false)}><Button variant="ghost" className={`w-full justify-start ${isDark ? 'text-white/50 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>Contact</Button></Link>
              {user ? (
                <>
                  <Link to={user.role === 'super_admin' ? '/admin' : user.role === 'authority' ? '/authority' : user.role === 'affiliate' ? '/affiliate' : '/dashboard'} onClick={() => setMobileOpen(false)}>
                    <Button variant="ghost" className={`w-full justify-start ${isDark ? 'text-white/50' : 'text-gray-600'}`}>{t('nav.dashboard')}</Button>
                  </Link>
                  {user.role === 'super_admin' && <Link to="/settings" onClick={() => setMobileOpen(false)}><Button variant="ghost" className={`w-full justify-start ${isDark ? 'text-white/50' : 'text-gray-600'}`}>Settings</Button></Link>}
                  <Button variant="ghost" onClick={() => { onLogout(); setMobileOpen(false); }} className="w-full justify-start text-red-400">{t('nav.logout')}</Button>
                </>
              ) : (
                <Link to="/auth" onClick={() => setMobileOpen(false)}><Button className="w-full bg-blue-500 hover:bg-blue-600 text-white">{t('nav.login')}</Button></Link>
              )}
              <div className="flex gap-2 pt-2">
                {['en', 'ar', 'de'].map(lng => (
                  <Button key={lng} variant="outline" size="sm" onClick={() => { changeLanguage(lng); setMobileOpen(false); }}
                    className={`${isDark ? 'border-white/[0.08]' : 'border-gray-200'} ${i18n.language === lng ? 'text-blue-500' : isDark ? 'text-white/40' : 'text-gray-400'}`}>{getLangLabel(lng)}</Button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}

function ProtectedRoute({ user, allowedRoles, children }) {
  if (!user) return <Navigate to="/auth" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (stored && token) {
      setUser(JSON.parse(stored));
      authAPI.me().then(res => {
        setUser(res.data);
        localStorage.setItem('user', JSON.stringify(res.data));
      }).catch(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      });
    }
    setLoading(false);
    authAPI.seedAdmin().catch(() => {});
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) return null;

  return (
    <>
      <Navbar user={user} onLogout={handleLogout} />
      <div className="pt-16 min-h-[calc(100vh-200px)]">
        <AnimatePresence mode="wait">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/usage-policy" element={<UsagePolicyPage />} />
            <Route path="/auth" element={user ? <Navigate to={user.role === 'super_admin' ? '/admin' : user.role === 'authority' ? '/authority' : user.role === 'affiliate' ? '/affiliate' : '/dashboard'} /> : <AuthPage onLogin={setUser} />} />
            <Route path="/submit" element={<SubmitRequestPage user={user} />} />
            <Route path="/track" element={<TrackingPage />} />
            <Route path="/track/:code" element={<TrackingPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<Navigate to="/submit" />} />
            <Route path="/admin" element={<ProtectedRoute user={user} allowedRoles={['super_admin']}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute user={user} allowedRoles={['super_admin']}><SettingsPage /></ProtectedRoute>} />
            <Route path="/authority" element={<ProtectedRoute user={user} allowedRoles={['authority']}><AuthorityPanel /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute user={user} allowedRoles={['subscriber']}><SubscriberDashboard user={user} /></ProtectedRoute>} />
            <Route path="/affiliate" element={<ProtectedRoute user={user} allowedRoles={['affiliate']}><AffiliateDashboard /></ProtectedRoute>} />
            <Route path="/appeals/:id" element={<ProtectedRoute user={user}><AppealDetailPage /></ProtectedRoute>} />
          </Routes>
        </AnimatePresence>
      </div>
      <Footer />
      <Toaster position="top-right" richColors />
    </>
  );
}

function App() {
  const [theme, setThemeState] = useState('midnight');

  // Load theme from backend (admin-controlled)
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/public-theme`)
      .then(r => r.json())
      .then(data => {
        if (data.theme && ['midnight', 'aurora', 'cyber', 'ember'].includes(data.theme)) {
          setThemeState(data.theme);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    document.documentElement.classList.remove('theme-midnight', 'theme-aurora', 'theme-cyber', 'theme-ember', 'light', 'dark');
    document.documentElement.classList.add(`theme-${theme}`);
    document.documentElement.classList.add(theme === 'aurora' ? 'light' : 'dark');
  }, [theme]);

  const setTheme = (newTheme) => {
    if (['midnight', 'aurora', 'cyber', 'ember'].includes(newTheme)) {
      setThemeState(newTheme);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes: THEMES }}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ThemeContext.Provider>
  );
}

export default App;
